import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { request, ApiError } from '../lib/api-client.ts';
import type { CollectResult } from '../types/industry.ts';
import type { SourceDescriptor } from '../types/source.ts';

type Props = {
  source: SourceDescriptor;
  onCollected: () => void;
};

type RunStatus =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'running' }
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string };

const CONFIRM_AUTO_RESET_MS = 6000;
const RESULT_AUTO_RESET_MS = 5000;

/**
 * データソース1行。
 *
 * 削除/収集確認は **モーダルではなくインライン2段階ボタン**（Ryuichi の好み）。
 * 1回目「収集」→ 「本当に実行」に変身、6秒で自動キャンセル。
 *
 * unmount 後の setState 警告を防ぐため、setTimeout は mountedRef でガードし、
 * cleanup で全タイマーをクリアする。
 */
export const SourceRow = ({ source, onCollected }: Props): React.JSX.Element => {
  const [status, setStatus] = useState<RunStatus>({ kind: 'idle' });
  const mountedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const scheduleReset = (delay: number): void => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (mountedRef.current) setStatus({ kind: 'idle' });
    }, delay);
  };

  const startConfirm = (): void => {
    setStatus({ kind: 'confirming' });
    scheduleReset(CONFIRM_AUTO_RESET_MS);
  };

  const runCollect = async (): Promise<void> => {
    if (!source.collect_endpoint) return;
    setStatus({ kind: 'running' });
    try {
      const res = await request<CollectResult>(source.collect_endpoint, { method: 'POST' });
      if (!mountedRef.current) return;
      setStatus({
        kind: 'success',
        message: `${res.signals_inserted} signals 投入（run_id=${res.run_id}）`,
      });
      onCollected();
      scheduleReset(RESULT_AUTO_RESET_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof ApiError ? err.message : '実行に失敗しました';
      setStatus({ kind: 'error', message });
      scheduleReset(RESULT_AUTO_RESET_MS);
    }
  };

  return (
    <li style={rowStyle}>
      <div style={metaStyle}>
        <strong>{source.display_name}</strong>
        <small style={descStyle}>{source.description}</small>
      </div>
      <div style={actionStyle}>{renderAction(source, status, startConfirm, runCollect)}</div>
    </li>
  );
};

const renderAction = (
  source: SourceDescriptor,
  status: RunStatus,
  startConfirm: () => void,
  runCollect: () => void
): React.JSX.Element => {
  // implemented を先にチェック: 未実装ソースは collect_endpoint の有無に関わらず pending メッセージを優先
  if (!source.implemented) return <span style={mutedStyle}>{source.pending_reason ?? `Phase ${source.phase} で実装予定`}</span>;
  if (!source.collect_endpoint) return <span style={mutedStyle}>（管理UIから直接実行不可）</span>;
  if (status.kind === 'running') return <span>実行中…</span>;
  if (status.kind === 'success') return <span style={successStyle}>{status.message}</span>;
  if (status.kind === 'error') return <span style={errorStyle}>{status.message}</span>;
  if (status.kind === 'confirming') {
    return (
      <button type="button" onClick={runCollect} style={dangerBtnStyle}>
        本当に実行
      </button>
    );
  }
  return (
    <button type="button" onClick={startConfirm} style={primaryBtnStyle}>
      収集
    </button>
  );
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderBottom: '1px solid #eee',
  listStyle: 'none',
};

const metaStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };
const descStyle: React.CSSProperties = { color: '#777', fontSize: 12 };
const actionStyle: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'center' };
const mutedStyle: React.CSSProperties = { color: '#999', fontSize: 13 };
const successStyle: React.CSSProperties = { color: '#27ae60', fontSize: 13 };
const errorStyle: React.CSSProperties = { color: '#c0392b', fontSize: 13 };
const primaryBtnStyle: React.CSSProperties = { padding: '6px 12px', fontSize: 13, cursor: 'pointer' };
const dangerBtnStyle: React.CSSProperties = { padding: '6px 12px', fontSize: 13, background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' };
